import { ref, readonly } from 'vue'
import { v2 } from '@/services/api.js'

// Single source of truth for the four access-rule types a flag's `rules`
// array can contain — key must match FeatureFlag.RuleType on the server.
// Both the rule-editing tabs (FeatureFlagForm.vue) and the rule-summary
// badges (FeatureFlags.vue) key off this instead of each hardcoding their
// own copy of the same four strings.
export const RULE_TYPES = [
  { key: 'user', label: 'Users', singular: 'user', icon: 'heroicons:user', color: 'iris', tone: 'info' },
  { key: 'manager', label: 'Managers', singular: 'manager', icon: 'heroicons:user-group', color: 'aqua', tone: 'caution' },
  { key: 'organization', label: 'Organizations', singular: 'organization', icon: 'heroicons:building-office-2', color: 'mantis', tone: 'positive' },
  { key: 'role', label: 'Roles', singular: 'role', icon: 'heroicons:identification', color: 'peach', tone: 'warning' }
]

// Keys of the Feature Flags the current user has access to. Shared module-level
// state (like useAbility.js) so any component can check a flag without prop drilling.
const enabledFlagKeys = ref([])

// Admin-only: every Feature Flag, including its access rules. Populated on the
// Feature Flags admin page.
const allFeatureFlags = ref([])

// Admin-only: distinct Department/Users_Role__c values pulled live from
// Salesforce, used to populate the "Organizations"/"Roles" pickers.
const fieldOptions = ref({ departments: [], roles: [] })

/**
 * Fetch the current user's enabled Feature Flags.
 */
async function fetchUserFeatureFlags() {
  const response = await v2.get('/user/feature-flags')
  enabledFlagKeys.value = response.data
  return response.data
}

/**
 * @param {string} key
 * @returns {boolean}
 */
function isFeatureEnabled(key) {
  return enabledFlagKeys.value.includes(key)
}

/**
 * Fetch every Feature Flag (Admin only).
 */
async function fetchAllFeatureFlags() {
  const response = await v2.get('/feature-flags')
  allFeatureFlags.value = response.data
  return response.data
}

/**
 * Create a new Feature Flag (Admin only).
 * @param {{ key: string, title: string, description?: string, enabled: boolean, active?: boolean, rules: Array }} flag
 */
async function createFeatureFlag(flag) {
  const response = await v2.post('/feature-flags', flag)
  allFeatureFlags.value = [...allFeatureFlags.value, response.data]
  return response.data
}

/**
 * Update an existing Feature Flag (Admin only).
 * @param {number} id
 * @param {{ title: string, description?: string, enabled: boolean, active?: boolean, rules: Array }} flag
 */
async function updateFeatureFlag(id, flag) {
  const response = await v2.put(`/feature-flags/${id}`, flag)
  allFeatureFlags.value = allFeatureFlags.value.map(f => f.id === id ? response.data : f)
  return response.data
}

/**
 * Delete a Feature Flag (Admin only).
 * @param {number} id
 */
async function deleteFeatureFlag(id) {
  await v2.delete(`/feature-flags/${id}`)
  allFeatureFlags.value = allFeatureFlags.value.filter(f => f.id !== id)
}

/**
 * Fetch the Department/Role values available for access rules, sourced live
 * from Salesforce (Admin only).
 */
async function fetchFieldOptions() {
  const response = await v2.get('/feature-flags/field-options')
  fieldOptions.value = response.data
  return response.data
}

export default function useFeatureFlags() {
  return {
    // State
    enabledFlagKeys: readonly(enabledFlagKeys),
    allFeatureFlags: readonly(allFeatureFlags),
    fieldOptions: readonly(fieldOptions),

    // Any authenticated user
    fetchUserFeatureFlags,
    isFeatureEnabled,

    // Admin only
    fetchAllFeatureFlags,
    createFeatureFlag,
    updateFeatureFlag,
    deleteFeatureFlag,
    fetchFieldOptions
  }
}
